/* eslint-disable no-param-reassign */
import React from 'react';
import sanitizeHtml from 'sanitize-html';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Typography } from '@mui/material';
import {
  Pause,
  PlayArrowRounded,
  FavoriteBorderOutlined,
} from '@mui/icons-material';
import FEATURE_FLAG from '@src/constants/featureFlags.json';
import Feature from '@src/components/Feature';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import route from '@src/constants/route';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import BadgeAvatar from '@src/components/BadgeAvatar';
import { capitalize } from 'lodash';
import { checkVietNam } from '@src/utils/checkCountry';
import {
  FEATURE,
  PACKAGE_CODE_V3,
  PACKAGE_LEVEL,
  PACKAGE_TYPE,
} from '@src/constants/package';
import { INIT_NOTIFICATION_DIALOG } from '@src/constants/dialog';
import { canUseEOLVoice } from '@src/services/voice';
import { TTS_VERSION } from '@src/constants/tts';
import { VOICE_LEVEL, VOICE_PROVIDER } from '@src/constants/voice';
import { COLOR } from '@src/styles/color';
import { checkFeaturePermission } from '@src/services/tts';
import actions from '@src/redux/actions';
import useUserInfo from '@src/hooks/useTtsUser';
import CrownImg from '@src/assets/images/crown.png';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useFeatureFlags from '@src/hooks/useFeatureFlags';

import { StyledVoiceItem } from './index.style';
import VoiceTag from './VoiceTag';

const VoiceItem = ({
  voice,
  activeVoiceId,
  canUseVoiceCloning,
  usingPackageType,
  onChangePlayAudio,
  onSelectedVoice,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { getFeatureValue } = useFeatureFlags();
  const { usingPackage } = useSelector((state) => state.user);
  const { ttsUser } = useUserInfo();
  const { openSentences } = useSelector((state) => state.synthesisRequest);
  const { defaultGlobalVoice } = useSelector((state) => state.voice);
  const { usingDubbingPackage } = useSelector((state) => state.user);
  const isVietNam = checkVietNam();
  const history = useHistory();
  const isDefaultGlobalVoice =
    !isVietNam &&
    usingPackage?.level === PACKAGE_LEVEL.BASIC &&
    voice?.code === defaultGlobalVoice;

  const useUserEolDate = getFeatureValue(
    FEATURE_KEYS.UPDATE_VOICES_BY_USER_EOL_DATE,
    {
      userId: ttsUser.id,
    },
  );

  const canUseVoice = () => {
    const isVoiceCloning =
      voice?.provider && voice?.provider === VOICE_PROVIDER.VBEE_VOICE_CLONING;

    if (isVoiceCloning && !canUseVoiceCloning) return false;

    if (!voice?.features?.length) return true;
    // old packages version do not have field version
    const isNewPackageVersion =
      !usingPackage?.version || usingPackage?.version !== 'V1';

    let usingPackageByType =
      usingPackageType === PACKAGE_TYPE.DUBBING ? ttsUser?.dubbing : ttsUser;

    // new package can use both studio and dubbing
    if (isNewPackageVersion) {
      usingPackageByType = ttsUser;
    }

    if (!usingDubbingPackage && isDefaultGlobalVoice) return true;

    const eolVoice = voice?.eolDate;
    if (eolVoice && useUserEolDate && canUseEOLVoice({ voice, user: ttsUser }))
      return true;

    return voice.features.every((item) =>
      checkFeaturePermission(usingPackageByType?.features, item),
    );
  };

  const handleLimitedFeatureVoiceDialog = () => {
    const isBasicGlobalVoice = voice?.features?.includes(
      FEATURE.BASIC_GLOBAL_VOICE,
    );
    const isAdvancedGlobalVoice = voice?.features?.includes(
      FEATURE.ADVANCED_GLOBAL_VOICE,
    );

    if (isAdvancedGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            history.push(
              `${route.PAYMENT}?package=${PACKAGE_CODE_V3.STUDIO_VIP_YEAR_V3}`,
            );
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );

      return;
    }

    if (isBasicGlobalVoice) {
      dispatch(
        actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
          description: t('voiceUpgradeSpecialOrVip'),
          img: CrownImg,
          classNameImg: 'custom-img',
          maxWidth: 'sm',
          onUpgrade: () => {
            history.push(route.PAYMENT);
            dispatch(actions.limitedFeaturesDialog.hideLimitedFeaturesDialog());
          },
        }),
      );
      return;
    }

    dispatch(
      actions.limitedFeaturesDialog.showLimitedFeaturesDialog({
        description: t('limitedFeatureDesc', { feature: t('useThisVoice') }),
      }),
    );
  };

  const handleChangePlayAudio = (e) => {
    e.stopPropagation();
    onChangePlayAudio(voice);
  };

  const handleCloseNotificationDialog = () =>
    dispatch(
      actions.dialog.displayDialog({
        notificationDialog: INIT_NOTIFICATION_DIALOG,
      }),
    );

  const handleSelectedVoice = () => {
    if (voice?.version === TTS_VERSION.EMPHASIS && openSentences) {
      dispatch(
        actions.dialog.displayDialog({
          notificationDialog: {
            name: 'cannotUseVoice',
            title: t('cannotUseVoice'),
            description: t('cannotUseVoiceDes'),
            variant: 'warning',
            open: true,
            actionComponent: (
              <Button
                variant="contained"
                onClick={handleCloseNotificationDialog}
              >
                {t('understood')}
              </Button>
            ),
          },
        }),
      );
      return;
    }

    if (canUseVoice()) {
      onSelectedVoice(voice);
    } else {
      handleLimitedFeatureVoiceDialog();
    }
  };

  const styles =
    !!voice?.styles?.length && voice.styles.map((item) => t(item)).join(' - ');

  // const getImportantTagNameAdvancedVoice = () => {
  //   if (useVoiceCreditsByLevel && voice?.level === VOICE_LEVEL.ADVANCED) {
  //     return t(voice.level);
  //   }
  //   return '';
  // };

  // const getImportantTagName = () => {
  //   if (voice?.languageCode === VOICE_LEVEL.MULTILINGUAL)
  //     return t('multilingual');
  //   if (voice?.beta) return capitalize(t('beta'));
  //   if (voice?.level === VOICE_LEVEL.PREMIUM) return t(voice.level);

  //   return '';
  // };
  // const getImportantTagBg = () => {
  //   if (voice?.languageCode === VOICE_LEVEL.MULTILINGUAL)
  //     return COLOR.indigoColor;
  //   if (voice.id === activeVoiceId) return COLOR.white;
  //   if (openSentences && voice?.version === TTS_VERSION.EMPHASIS)
  //     return COLOR.lightText;
  //   if (voice?.beta) return COLOR.skyBlue;
  //   return COLOR.primary;
  // };

  const getLabels = () => {
    // Display level label except level BASIC
    const isAboutToBeRemoved = voice?.eolDate
      ? moment().isBefore(voice?.eolDate)
      : false;
    const daysLeftBeforeVoiceInactive = moment(voice?.eolDate).diff(
      moment(),
      'days',
    );
    const eolTagNameData = {
      textColor: COLOR.redPrimary,
      bgColor: COLOR.bgRedPrimary,
      title: isAboutToBeRemoved
        ? t(daysLeftBeforeVoiceInactive ? 'dayNumber' : 'today', {
            daysLeftBeforeVoiceInactive,
          })
        : undefined,
      icon: <RunningWithErrorsRoundedIcon className="returning-error-icon" />,
      contentTooltip: (
        <Typography
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(
              t('discontinuedAfterTime', {
                date: moment(voice?.eolDate)?.format('DD/MM/YYYY'),
                name: voice?.name || '',
              }),
              {
                allowedTags: ['b', 'span'],
                allowedAttributes: {
                  '*': ['style'],
                },
              },
            ),
          }}
        />
      ),
    };
    const levelTagNameData = {
      textColor: COLOR.darkBlue,
      bgColor: COLOR.primary,
      title: voice?.level !== VOICE_LEVEL.BASIC ? t(voice.level) : undefined,
    };
    const betaTagNameData = {
      textColor: COLOR.blueV2,
      bgColor: COLOR.skyBlue,
      title: voice?.beta ? capitalize(t('beta')) : undefined,
    };

    const isChoosingVoice = voice.id === activeVoiceId;
    if (isChoosingVoice) {
      levelTagNameData.bgColor = COLOR.white;
      levelTagNameData.textColor = COLOR.black.default;

      betaTagNameData.bgColor = COLOR.white;
      betaTagNameData.textColor = COLOR.black.default;
    }

    return [eolTagNameData, betaTagNameData, levelTagNameData].filter(
      (item) => item?.title,
    );
  };

  return (
    <StyledVoiceItem
      disabledVoice={openSentences && voice?.version === TTS_VERSION.EMPHASIS}
      active={voice.id === activeVoiceId}
      playingAudio={voice.playStatus}
      onClick={handleSelectedVoice}
      variant="outlined"
      data-id="select-voice"
      data-voice-code={voice?.code}
      aria-label={`${voice.name} voice`}
    >
      <VoiceTag getLabels={getLabels()} />
      <div className="voice-info">
        <BadgeAvatar
          img={voice.roundImage}
          smallImg={voice && voice.language && voice.language.roundImage}
          type="image"
        />
        <div className="detail-voice">
          <Typography variant="h6" className="voice-name">
            {voice.name}
            {voice?.creditFactor > 1 && (
              <span className="x2-char-tag">x{voice?.creditFactor}</span>
            )}
          </Typography>
          <Typography variant="subtitle1" className="features">
            {styles}
          </Typography>
        </div>
      </div>
      <IconButton
        onClick={handleChangePlayAudio}
        aria-label="play"
        data-id="preview-voice"
        data-voice-code={voice?.code}
      >
        <Feature name={FEATURE_FLAG.NEW_VOICE_DIALOG}>
          <FavoriteBorderOutlined className="favorite-icon" color="error" />
        </Feature>
        {voice.playStatus ? (
          <Pause className="play-icon" fontSize="large" />
        ) : (
          <PlayArrowRounded className="play-icon" fontSize="large" />
        )}
      </IconButton>
    </StyledVoiceItem>
  );
};
export default VoiceItem;
